import { Injectable } from '@angular/core';
import { ADMIN_MODULE_IDS } from 'app/core/admin-module-ids';

export interface UrlPermissionMapping {
    pattern: RegExp;
    moduleId: number;
    viewPermission: string;
    editPermission: string;
}

export interface PermissionResult {
    moduleId: number;
    requiredPermission: string;
}

@Injectable({
    providedIn: 'root'
})
export class UrlPermissionMappingService {
    
    // Map URL patterns to moduleId and permission type
    private urlMappings: UrlPermissionMapping[] = [
        // Pages Group
        { pattern: /^\/dashboard/, moduleId: 1, viewPermission: 'View', editPermission: 'View' },
        { pattern: /^\/data-usage-analysis/, moduleId: 2, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/change-logs/, moduleId: 61, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/activity-logs/, moduleId: 1, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/error-logs/, moduleId: 63, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/scanned-documents/, moduleId: 62, viewPermission: 'View', editPermission: 'Edit' },

        // Accounts Group — one module per screen since Docs/go-live/p86.
        // These sit ABOVE the generic /partner line on purpose: find() takes
        // the first match, and /^\/partner/ would swallow /partner-withdrawals.
        { pattern: /^\/partner-withdrawals/, moduleId: 68, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/partner-verifications/, moduleId: 69, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/partner-payouts/, moduleId: 70, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/sales-commission/, moduleId: 71, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/partner-tds-config/, moduleId: 72, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/partner-tds-settlement/, moduleId: 73, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/partner-form16a/, moduleId: 74, viewPermission: 'View', editPermission: 'Edit' },

        // Business Management Group
        { pattern: /^\/app-demo/, moduleId: 75, viewPermission: 'View', editPermission: 'Edit' },  // Demo Bookings (p88)
        { pattern: /^\/business/, moduleId: 3, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/partner/, moduleId: 4, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/leads/, moduleId: 5, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/inquiries/, moduleId: 6, viewPermission: 'View', editPermission: 'Edit' },

        // Plans & Subscriptions Group
        { pattern: /^\/plans/, moduleId: 7, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/coupon/, moduleId: 8, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/orders/, moduleId: 9, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/order-refunds/, moduleId: 10, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/wallet/, moduleId: 11, viewPermission: 'View', editPermission: 'Edit' },

        // Blogs Group
        { pattern: /^\/blogs/, moduleId: 12, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/blogtype/, moduleId: 13, viewPermission: 'View', editPermission: 'Edit' },

        // Support Center Group
        { pattern: /^\/tickets/, moduleId: 14, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/chat-support/, moduleId: 15, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/support-actions/, moduleId: 16, viewPermission: 'View', editPermission: 'Edit' },

        // Testimonials & Feedback Group
        { pattern: /^\/testimonital/, moduleId: 17, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/feedback/, moduleId: 18, viewPermission: 'View', editPermission: 'Edit' },

        // Notifications Group
        { pattern: /^\/notifications/, moduleId: 19, viewPermission: 'View', editPermission: 'Edit' },

        // User Management Group
        { pattern: /^\/users/, moduleId: 21, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/user-roles/, moduleId: 22, viewPermission: 'View', editPermission: 'Edit' },

        // Reports Group (single permission for entire section)
        { pattern: /^\/business-report/, moduleId: 23, viewPermission: 'View', editPermission: 'View' },
        { pattern: /^\/business-paidunpaid-report/, moduleId: 23, viewPermission: 'View', editPermission: 'View' },
        { pattern: /^\/business-growth-report/, moduleId: 23, viewPermission: 'View', editPermission: 'View' },
        { pattern: /^\/business-revenue-report/, moduleId: 23, viewPermission: 'View', editPermission: 'View' },
        { pattern: /^\/business-user-engagement-report/, moduleId: 23, viewPermission: 'View', editPermission: 'View' },
        { pattern: /^\/business-active-inactive-report/, moduleId: 23, viewPermission: 'View', editPermission: 'View' },
        { pattern: /^\/business-activity-report/, moduleId: 23, viewPermission: 'View', editPermission: 'View' },
        { pattern: /^\/business-registered-report/, moduleId: 23, viewPermission: 'View', editPermission: 'View' },
        { pattern: /^\/otp-sends/, moduleId: 23, viewPermission: 'View', editPermission: 'View' },  // OTP Sends (Analytics & Logs) + per-customer detail — read-only, on the Reports grant
        { pattern: /^\/business-new-vs-existing-report/, moduleId: 23, viewPermission: 'View', editPermission: 'View' },
        { pattern: /^\/opentickets-report/, moduleId: 23, viewPermission: 'View', editPermission: 'View' },
        { pattern: /^\/ticket-resolution-time-report/, moduleId: 23, viewPermission: 'View', editPermission: 'View' },
        { pattern: /^\/ticket-escalation-report/, moduleId: 23, viewPermission: 'View', editPermission: 'View' },
        { pattern: /^\/ticket-average-response-time-report/, moduleId: 23, viewPermission: 'View', editPermission: 'View' },
        { pattern: /^\/total-orders-report/, moduleId: 23, viewPermission: 'View', editPermission: 'View' },
        { pattern: /^\/pending-orders-report/, moduleId: 23, viewPermission: 'View', editPermission: 'View' },
        { pattern: /^\/order-conversion-rate-report/, moduleId: 23, viewPermission: 'View', editPermission: 'View' },
        { pattern: /^\/signup-to-sale-funnel-report/, moduleId: 23, viewPermission: 'View', editPermission: 'View' },
        { pattern: /^\/trial-conversion-report/, moduleId: 23, viewPermission: 'View', editPermission: 'View' },
        { pattern: /^\/sales-by-trial-report/, moduleId: 23, viewPermission: 'View', editPermission: 'View' },
        { pattern: /^\/cancelled-failed-order-report/, moduleId: 23, viewPermission: 'View', editPermission: 'View' },
        { pattern: /^\/plan-subscription-report/, moduleId: 23, viewPermission: 'View', editPermission: 'View' },
        { pattern: /^\/plan-renewal-expiration-report/, moduleId: 23, viewPermission: 'View', editPermission: 'View' },
        { pattern: /^\/plan-expiring-report/, moduleId: 23, viewPermission: 'View', editPermission: 'View' },
        { pattern: /^\/plan-expiring-summary-by-date-report/, moduleId: 23, viewPermission: 'View', editPermission: 'View' },
        { pattern: /^\/plan-expiring-revenue-report/, moduleId: 23, viewPermission: 'View', editPermission: 'View' },
        { pattern: /^\/plan-expired-report/, moduleId: 23, viewPermission: 'View', editPermission: 'View' },
        { pattern: /^\/plan-expired-summary-by-date-report/, moduleId: 23, viewPermission: 'View', editPermission: 'View' },
        { pattern: /^\/plan-expired-revenue-report/, moduleId: 23, viewPermission: 'View', editPermission: 'View' },
        { pattern: /^\/inquiries-overview-report/, moduleId: 23, viewPermission: 'View', editPermission: 'View' },
        { pattern: /^\/inquiry-status-distribution-report/, moduleId: 23, viewPermission: 'View', editPermission: 'View' },
        { pattern: /^\/inquiry-conversion-report/, moduleId: 23, viewPermission: 'View', editPermission: 'View' },
        { pattern: /^\/feedback-status-report/, moduleId: 23, viewPermission: 'View', editPermission: 'View' },
        { pattern: /^\/feedback-sentiment-analysis-report/, moduleId: 23, viewPermission: 'View', editPermission: 'View' },
        { pattern: /^\/feedback-response-time-report/, moduleId: 23, viewPermission: 'View', editPermission: 'View' },
        { pattern: /^\/feedback-resolution-report/, moduleId: 23, viewPermission: 'View', editPermission: 'View' },

        // Admin Settings Group
        { pattern: /^\/settings/, moduleId: 24, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/website-setting/, moduleId: 25, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/server-health/, moduleId: 67, viewPermission: 'View', editPermission: 'View' },
        { pattern: /^\/third-party-services/, moduleId: 24, viewPermission: 'View', editPermission: 'View' },  // Third-Party Services — read-only, on the Settings grant
        { pattern: /^\/app-maintenance/, moduleId: 64, viewPermission: 'View', editPermission: 'Edit' },

        // Notifications Group — the four campaign/template screens share 66
        { pattern: /^\/campaigns/, moduleId: 66, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/notification-methods/, moduleId: 66, viewPermission: 'View', editPermission: 'Edit' },

        // Help & Support Group
        { pattern: /^\/FAQs/, moduleId: 26, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/support-article-types/, moduleId: 27, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/support-articles/, moduleId: 28, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/ticket-topics/, moduleId: 29, viewPermission: 'View', editPermission: 'Edit' },

        // Business Settings Group
        { pattern: /^\/invoice-templates/, moduleId: 30, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/business-roles/, moduleId: 31, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/business-user-settings/, moduleId: 32, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/business-settings/, moduleId: 33, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/email-templates/, moduleId: 60, viewPermission: 'View', editPermission: 'Edit' },

        // Module Management Group
        { pattern: /^\/module-group/, moduleId: 34, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/modules/, moduleId: 35, viewPermission: 'View', editPermission: 'Edit' },

        // Feature Management Group
        { pattern: /^\/features/, moduleId: 36, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/feature-category/, moduleId: 37, viewPermission: 'View', editPermission: 'Edit' },
        // Admin permission catalog (a_modulegroups / a_modules). Anchored, so
        // /^\/module-group/ and /^\/modules/ above don't catch these.
        { pattern: /^\/admin-module-group/, moduleId: ADMIN_MODULE_IDS.ADMIN_MODULE_GROUPS, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/admin-modules/, moduleId: ADMIN_MODULE_IDS.ADMIN_MODULES, viewPermission: 'View', editPermission: 'Edit' },

        // Master Data Group (single permission for entire section)
        { pattern: /^\/units/, moduleId: 38, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/expense-categories/, moduleId: 43, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/gst-slabs/, moduleId: 44, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/hsn-codes/, moduleId: 45, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/business-categories/, moduleId: 46, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/category-module-map/, moduleId: 76, viewPermission: 'View', editPermission: 'View' },  // Category Module Map (p91)
        { pattern: /^\/business-types/, moduleId: 47, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/business-questions/, moduleId: 48, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/regions/, moduleId: 49, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/leave-types/, moduleId: 50, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/currencies/, moduleId: 51, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/campaign-channels/, moduleId: 52, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/whatsapp-templates/, moduleId: 66, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/sms-templates/, moduleId: 66, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/tax-types/, moduleId: 53, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/platforms/, moduleId: 54, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/auth-providers/, moduleId: 55, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/banks/, moduleId: 56, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/plan-users/, moduleId: 57, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/plan-companies/, moduleId: 58, viewPermission: 'View', editPermission: 'Edit' },
        { pattern: /^\/stock-notes/, moduleId: 59, viewPermission: 'View', editPermission: 'Edit' },
    ];

    /**
     * Get permission requirements for a given URL
     * @param url - The URL to check
     * @returns Permission requirements or null if no mapping found
     */
    getPermissionForUrl(url: string): PermissionResult | null {
        // Find matching pattern
        const mapping = this.urlMappings.find(m => m.pattern.test(url));
        if (!mapping) return null;

        // Determine permission type based on URL patterns
        const requiredPermission = this.determinePermissionType(url, mapping);
        
        return { 
            moduleId: mapping.moduleId, 
            requiredPermission 
        };
    }

    /**
     * Determine the required permission type based on URL patterns
     * @param url - The URL to analyze
     * @param mapping - The matched mapping
     * @returns The required permission type
     */
    private determinePermissionType(url: string, mapping: UrlPermissionMapping): string {
        // Check for edit/add patterns
        if (url.includes('/add') || 
            url.includes('/edit/') || 
            url.includes('/mode/edit') ||
            url.includes('/mode/add') ||
            url.match(/\/[^\/]+\/[^\/]+\/\d+$/)) { // Pattern like /module/edit/123
            return mapping.editPermission;
        }
        
        // Default to view permission
        return mapping.viewPermission;
    }

    /**
     * Check if a URL should be excluded from permission checking
     * @param url - The URL to check
     * @returns True if URL should be excluded
     */
    isExcludedUrl(url: string): boolean {
        const excludedPatterns = [
            /^\/profile/,
            /^\/reset-password/,
            /^\/sign-out/,
            /^\/unlock-session/,
            /^\/pages\//,
            /^\/apps\//,
            /^\/ui\//
        ];

        return excludedPatterns.some(pattern => pattern.test(url));
    }
}
